import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useNavigate,
  useOutletContext,
  useSearchParams,
} from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  TextField,
  LinearProgress,
  CircularProgress,
  Button,
  Collapse,
} from "@mui/material";
import {
  IconChevronDown,
  IconChevronLeft,
  IconCamera,
  IconBluetooth,
  IconCheck,
  IconX,
  IconPackages,
  IconScan,
  IconTag,
  IconTruckDelivery,
} from "@tabler/icons-react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { markingApi, ymAssembliesApi } from "../api";
import ManufacturerCatalogPanel, {
  CatalogProductCard,
} from "../components/ManufacturerCatalogPanel";
import {
  groupYmProductsForCatalog,
  normalizeCatalogsIndex,
  useManufacturerCatalogView,
} from "../utils/assemblyCatalogView";
import {
  bannerSx,
  bannerTextSx,
  bannerTitleSx,
  camera,
  barIconActiveSx,
  barIconSx,
  cabinetChipSx,
  cabinetChipTextSx,
  cardActiveSx,
  cardDoneSx,
  cardSx,
  counterPillSx,
  counterPillTextSx,
  ds,
  dsText,
  mutedTextSx,
  scannerFieldSx,
  sectionLabelSx,
  shellSx,
  softChipSx,
  topBarSx,
} from "../theme/keeperTheme";
import {
  MARKING_ZXING_HINTS,
  focusInputNoScroll,
  getMarkingScanRejectMessage,
  markingCodeForApply,
  remapRuLayoutToEn,
} from "../utils/markingScan";
import { shipmentDayLabel } from "../utils/shipmentDay";

const CAMERA_H = 200;
const SCANNER_STRIP_H = 88;
const CRPT_DEBOUNCE_CAMERA_MS = 380;
const CRPT_DEBOUNCE_SCANNER_MS = 90;
/** Очередь обновляем сама по себе: её же меняют заказы и другой кладовщик. */
const POLL_LINES_MS = 20000;

function normalizeScan(raw) {
  const s = (raw || "").trim();
  if (!s) return "";
  return /[а-яА-ЯёЁ]/.test(s) ? remapRuLayoutToEn(s) : s;
}

/**
 * Строки сборки → карточки по заказам, самый срочный дедлайн первым.
 * need/done считаем только по позициям с маркировкой (CIS и CIS_OPTIONAL);
 * requiredLeft — сколько не хватает обязательных (CIS): без них Маркет раскладку не примет.
 */
function groupByOrder(lines) {
  const byOrder = new Map();
  for (const ln of lines || []) {
    const id = String(ln.order_id);
    if (!byOrder.has(id)) {
      byOrder.set(id, {
        order_id: ln.order_id,
        shipment_date: ln.shipment_date || null,
        ordered_at: ln.ordered_at || null,
        products: [],
        need: 0,
        done: 0,
        requiredLeft: 0,
        otherIdentifiers: [],
      });
    }
    const g = byOrder.get(id);
    const qty = Number(ln.line_qty) || 0;
    const need = ln.marking ? qty : 0;
    const done = Math.min(Number(ln.scanned_qty) || 0, need);
    g.products.push({ ...ln, qty, need, done });
    g.need += need;
    g.done += done;
    if (ln.marking === "required") g.requiredLeft += need - done;
    for (const t of ln.other_identifiers || []) {
      if (!g.otherIdentifiers.includes(t)) g.otherIdentifiers.push(t);
    }
  }
  return Array.from(byOrder.values()).sort((a, b) =>
    String(a.shipment_date || "9999").localeCompare(
      String(b.shipment_date || "9999"),
    ),
  );
}

/** Дата отгрузки Маркета: «YYYY-MM-DD» или «YYYY-MM-DDTHH:MM[:SS]» — без перевода зоны. */
function shipmentLabel(value) {
  const m = String(value || "").match(
    /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/,
  );
  if (!m) return "";
  return `отгрузка ${m[3]}.${m[2]}${m[4] ? ` ${m[4]}:${m[5]}` : ""}`;
}

/** Время оформления заказа: сервер отдаёт ISO с зоной, показываем в зоне устройства. */
function orderedLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `заказ ${p(d.getDate())}.${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** Итог проверки кодов в строку для кладовщика. */
function checkMessage(orderId, res) {
  if (res?.ready_to_ship) {
    return {
      tone: "success",
      text: res.codes_sent
        ? `Маркет принял ${res.codes_sent} КИЗ по заказу ${orderId}. Можно в «Готов к отгрузке».`
        : `Раскладка заказа ${orderId} передана. Можно в «Готов к отгрузке».`,
    };
  }
  if (res?.invalid?.length) {
    return {
      tone: "error",
      text: `Заказ ${orderId}: ${res.invalid.length} КИЗ не прошли проверку. Снимите их и отсканируйте другие.`,
    };
  }
  if (res?.pending) {
    return {
      tone: "warning",
      text: `Маркет ещё проверяет коды по заказу ${orderId}. Нажмите «Проверить» через минуту.`,
    };
  }
  return {
    tone: "warning",
    text: `Заказ ${orderId}: не все обязательные КИЗ подтверждены Маркетом.`,
  };
}

/**
 * Сборка Яндекс Маркета: кладовщик сканирует КИЗ в произвольном порядке.
 *
 * Заказ он не сканирует — сервер сам находит позицию по GTIN из кода и отдаёт код
 * заказу с ближайшей отгрузкой. Когда набор собран, раскладка с кодами уходит
 * в Маркет одним запросом, Маркет проверяет коды, затем «Готов к отгрузке» и ярлык.
 */
export default function YmAssembliesStartPage() {
  const { registerToolbarRefresh } = useOutletContext() || {};
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedCabinetId = searchParams.get("cabinet") || "";
  const selectedCabinetName = searchParams.get("cabinet_name") || "";

  const videoRef = useRef(null);
  const scanControlsRef = useRef(null);
  const scannerInputRef = useRef(null);
  const checkTimerRef = useRef(null);
  const lastProcessedScanRef = useRef("");
  const scanInFlightRef = useRef(false);
  const lastPointerDownTargetRef = useRef(null);

  const [inputMode, setInputMode] = useState("scanner");
  const inputModeRef = useRef(inputMode);
  inputModeRef.current = inputMode;

  const [cameraError, setCameraError] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [decoderError, setDecoderError] = useState(null);
  const [scannedText, setScannedText] = useState("");

  const [crptLoading, setCrptLoading] = useState(false);
  const [crptPhase, setCrptPhase] = useState("idle");

  const [lines, setLines] = useState([]);
  const [linesLoading, setLinesLoading] = useState(true);
  const [linesError, setLinesError] = useState(null);
  const [remainingUnits, setRemainingUnits] = useState(0);
  const [catalogsIndex, setCatalogsIndex] = useState({});

  const [busyOrder, setBusyOrder] = useState("");
  const [statusMessage, setStatusMessage] = useState(null);
  const [statusTone, setStatusTone] = useState("info");
  const [lastOrder, setLastOrder] = useState(null);
  /** Результат последней проверки кодов по заказу: {orderId: {ready_to_ship, invalid, …}}. */
  const [checks, setChecks] = useState({});
  /** Заказы, переведённые в «Готов к отгрузке» в этой сессии экрана — для кнопки ярлыка. */
  const [shipped, setShipped] = useState({});

  const fetchLines = useCallback(
    async ({ showLoading = false } = {}) => {
      if (!selectedCabinetId) {
        if (showLoading) {
          setLinesLoading(false);
          setLinesError(
            "Не выбран кабинет Маркета. Вернитесь назад и выберите кабинет.",
          );
        }
        return;
      }
      if (showLoading) {
        setLinesLoading(true);
        setLinesError(null);
      }
      try {
        const data = await ymAssembliesApi.markingLines(selectedCabinetId);
        setLines(Array.isArray(data.lines) ? data.lines : []);
        setRemainingUnits(Number(data.remaining_units) || 0);
        setCatalogsIndex(normalizeCatalogsIndex(data.catalogs_index));
      } catch (e) {
        if (showLoading)
          setLinesError(e?.message || "Не удалось загрузить очередь сборки");
      } finally {
        if (showLoading) setLinesLoading(false);
      }
    },
    [selectedCabinetId],
  );

  useEffect(() => {
    fetchLines({ showLoading: true });
  }, [fetchLines]);

  useEffect(() => {
    if (!registerToolbarRefresh) return undefined;
    return registerToolbarRefresh(() => fetchLines({ showLoading: true }));
  }, [registerToolbarRefresh, fetchLines]);

  useEffect(() => {
    const id = window.setInterval(
      () => fetchLines({ showLoading: false }),
      POLL_LINES_MS,
    );
    return () => window.clearInterval(id);
  }, [fetchLines]);

  const orders = useMemo(() => groupByOrder(lines), [lines]);
  const readyOrders = useMemo(
    () => orders.filter((o) => o.done >= o.need),
    [orders],
  );
  const catalogProducts = useMemo(() => groupYmProductsForCatalog(lines), [lines]);
  const manufacturerView = useManufacturerCatalogView(catalogProducts, catalogsIndex);
  const {
    groupByManufacturer,
    setGroupByManufacturer,
    catalogStack,
    currentCatalogId,
    visibleCatalogs,
    filteredIncomplete,
    filteredComplete,
    handleEnterCatalog,
    handleCatalogBreadcrumbClick,
  } = manufacturerView;
  /** Заказы по дням отгрузки: кладовщик собирает товар партией на конкретную дату. */
  const dayGroups = useMemo(() => {
    const byDay = new Map();
    for (const o of orders) {
      const day = String(o.shipment_date || "").slice(0, 10);
      if (!byDay.has(day))
        byDay.set(day, { day, orders: [], need: 0, done: 0 });
      const g = byDay.get(day);
      g.orders.push(o);
      g.need += o.need;
      g.done += o.done;
    }
    return Array.from(byDay.values()).sort((a, b) =>
      (a.day || "9999").localeCompare(b.day || "9999"),
    );
  }, [orders]);
  /** Свёрнутые дни: {"2026-10-29": true}. По умолчанию развёрнут только ближайший. */
  const [collapsedDays, setCollapsedDays] = useState({});
  const isDayCollapsed = (day, index) =>
    Object.prototype.hasOwnProperty.call(collapsedDays, day)
      ? collapsedDays[day]
      : index > 0;

  // --- камера ---

  useEffect(() => {
    if (inputMode !== "camera") {
      setCameraReady(false);
      return undefined;
    }
    let stream;
    let videoEl = null;
    let cancelled = false;
    const start = async () => {
      setCameraError(null);
      setCameraReady(false);
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        videoEl = videoRef.current;
        if (videoEl) {
          videoEl.srcObject = stream;
          await videoEl.play().catch(() => {});
          if (!cancelled) setCameraReady(true);
        }
      } catch (e) {
        if (!cancelled) {
          setCameraError(
            e?.name === "NotAllowedError"
              ? "Нет доступа к камере. Разрешите в настройках браузера."
              : e?.message || "Не удалось включить камеру",
          );
        }
      }
    };
    start();
    return () => {
      cancelled = true;
      stream?.getTracks().forEach((t) => t.stop());
      if (videoEl) videoEl.srcObject = null;
    };
  }, [inputMode]);

  useEffect(() => {
    if (inputMode !== "camera" || !cameraReady || cameraError) return undefined;
    const videoEl = videoRef.current;
    if (!videoEl) return undefined;
    let cancelled = false;
    setDecoderError(null);
    const reader = new BrowserMultiFormatReader(MARKING_ZXING_HINTS);
    reader
      .decodeFromVideoElement(videoEl, (result) => {
        if (cancelled || !result) return;
        setScannedText(result.getText());
      })
      .then((controls) => {
        if (cancelled) {
          controls.stop();
          return;
        }
        scanControlsRef.current = controls;
      })
      .catch((e) => {
        if (!cancelled) setDecoderError(e?.message || "Ошибка сканера");
      });
    return () => {
      cancelled = true;
      const c = scanControlsRef.current;
      scanControlsRef.current = null;
      if (c) c.stop();
    };
  }, [inputMode, cameraReady, cameraError]);

  // --- фокус поля BT-сканера ---

  useEffect(() => {
    if (inputMode !== "scanner") return undefined;
    const focus = () => focusInputNoScroll(scannerInputRef.current);
    const t1 = window.setTimeout(focus, 50);
    const t2 = window.setTimeout(focus, 300);
    const onVis = () => {
      if (document.visibilityState === "visible") focus();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [inputMode]);

  useEffect(() => {
    const onPointerDown = (e) => {
      lastPointerDownTargetRef.current = e.target;
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () =>
      document.removeEventListener("pointerdown", onPointerDown, true);
  }, []);

  const clearScanField = useCallback(() => {
    setScannedText("");
    lastProcessedScanRef.current = "";
    if (inputModeRef.current === "scanner") {
      const inp = scannerInputRef.current;
      if (inp) inp.value = "";
      window.setTimeout(() => focusInputNoScroll(scannerInputRef.current), 50);
    }
  }, []);

  // --- скан КИЗ: Честный знак, затем привязка на сервере ---

  useEffect(() => {
    let cancelled = false;
    if (checkTimerRef.current) {
      clearTimeout(checkTimerRef.current);
      checkTimerRef.current = null;
    }
    const raw = normalizeScan(scannedText);
    if (!raw) {
      setCrptPhase("idle");
      setCrptLoading(false);
      return undefined;
    }
    if (scanInFlightRef.current) return undefined;
    if (lastProcessedScanRef.current === raw) return undefined;

    const rejectMsg = getMarkingScanRejectMessage(raw);
    if (rejectMsg) {
      lastProcessedScanRef.current = raw;
      setCrptPhase("error");
      setStatusTone("error");
      setStatusMessage(rejectMsg);
      return undefined;
    }

    setCrptPhase("debounce");
    setStatusMessage(null);
    const debounceMs =
      inputMode === "scanner"
        ? CRPT_DEBOUNCE_SCANNER_MS
        : CRPT_DEBOUNCE_CAMERA_MS;

    checkTimerRef.current = setTimeout(() => {
      checkTimerRef.current = null;
      (async () => {
        if (cancelled || scanInFlightRef.current) return;
        lastProcessedScanRef.current = raw;
        scanInFlightRef.current = true;
        setCrptPhase("loading");
        setCrptLoading(true);
        let crpt = null;
        try {
          crpt = await markingApi.trueApiResolve(raw);
        } catch (e) {
          // Честный знак недоступен — код всё равно отдаём серверу, GTIN он возьмёт из Data Matrix
          crpt = null;
        }
        try {
          const res = await ymAssembliesApi.scan({
            connection_id: Number(selectedCabinetId),
            marking_code: markingCodeForApply(raw),
            crpt_payload: crpt || undefined,
          });
          if (cancelled) return;
          setCrptPhase("ok");
          setStatusTone("success");
          setLastOrder(String(res.order_id));
          setStatusMessage(
            `${res.title || res.offer_id} → заказ ${res.order_id} ` +
              `(${res.scanned_qty} из ${res.line_qty})` +
              (res.order_complete ? " · заказ укомплектован" : ""),
          );
          void fetchLines({ showLoading: false });
        } catch (e) {
          if (cancelled) return;
          setCrptPhase("error");
          setStatusTone("error");
          setStatusMessage(e?.message || "Не удалось привязать код");
        } finally {
          scanInFlightRef.current = false;
          setCrptLoading(false);
          clearScanField();
        }
      })();
    }, debounceMs);

    return () => {
      cancelled = true;
      if (checkTimerRef.current) {
        clearTimeout(checkTimerRef.current);
        checkTimerRef.current = null;
      }
    };
  }, [scannedText, inputMode, selectedCabinetId, fetchLines, clearScanField]);

  // --- раскладка с КИЗ, проверка, «Готов к отгрузке», ярлык ---

  const runForOrder = async (orderId, step, extra = {}) => {
    const key = String(orderId);
    const connectionId = Number(selectedCabinetId);
    setBusyOrder(key);
    setStatusMessage(null);
    try {
      if (step === "apply" || step === "check") {
        const res =
          step === "apply"
            ? await ymAssembliesApi.applyMarking({
                connection_id: connectionId,
                order_id: orderId,
              })
            : await ymAssembliesApi.checkMarking(connectionId, orderId);
        setChecks((prev) => ({ ...prev, [key]: res }));
        const msg = checkMessage(orderId, res);
        setStatusTone(msg.tone);
        setStatusMessage(msg.text);
      } else if (step === "ship") {
        await ymAssembliesApi.ship({
          connection_id: connectionId,
          order_id: orderId,
        });
        setShipped((prev) => ({ ...prev, [key]: true }));
        setStatusTone("success");
        setStatusMessage(`Заказ ${orderId} готов к отгрузке. Печатайте ярлык.`);
        void fetchLines({ showLoading: false });
      } else if (step === "label") {
        const url = await ymAssembliesApi.labels(connectionId, orderId);
        window.open(url, "_blank", "noopener");
        setStatusTone("success");
        setStatusMessage(`Ярлык заказа ${orderId} открыт.`);
      } else if (step === "unscan") {
        const res = await ymAssembliesApi.unscan({
          connection_id: connectionId,
          ...(extra.scanId ? { scan_id: extra.scanId } : { order_id: orderId }),
        });
        if (extra.scanId) {
          setChecks((prev) => {
            const cur = prev[key];
            if (!cur) return prev;
            const drop = (list) =>
              (list || []).filter((c) => c.scan_id !== extra.scanId);
            return {
              ...prev,
              [key]: {
                ...cur,
                invalid: drop(cur.invalid),
                codes: drop(cur.codes),
              },
            };
          });
        } else {
          setChecks((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
          });
        }
        setStatusTone("info");
        setStatusMessage(
          `Снято кодов: ${res?.removed ?? 0}. Товар можно сканировать заново.`,
        );
        void fetchLines({ showLoading: false });
      }
    } catch (e) {
      setStatusTone("error");
      setStatusMessage(e?.message || "Ошибка Маркета");
    } finally {
      setBusyOrder("");
    }
  };

  const scanPipeline = useMemo(() => {
    if (statusTone === "error" && statusMessage) {
      return {
        active: true,
        indeterminate: false,
        value: 100,
        spin: false,
        ok: false,
        err: true,
      };
    }
    if (statusTone === "success" && statusMessage) {
      return {
        active: true,
        indeterminate: false,
        value: 100,
        spin: false,
        ok: true,
        err: false,
      };
    }
    if (crptLoading || crptPhase === "loading") {
      return {
        active: true,
        indeterminate: true,
        value: 50,
        spin: true,
        ok: false,
        err: false,
      };
    }
    if (crptPhase === "debounce") {
      return {
        active: true,
        indeterminate: true,
        value: 18,
        spin: true,
        ok: false,
        err: false,
      };
    }
    return {
      active: false,
      indeterminate: true,
      value: 0,
      spin: false,
      ok: false,
      err: false,
    };
  }, [statusTone, statusMessage, crptLoading, crptPhase]);

  const toggleInputMode = () => {
    setInputMode((m) => (m === "camera" ? "scanner" : "camera"));
    clearScanField();
    setCrptPhase("idle");
  };

  const handleScannerKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const el = e.target;
      const v = (el.value || "").trim();
      el.value = "";
      if (v) setScannedText(v);
    }
  };

  const handleScannerPaste = (e) => {
    const t = e.clipboardData?.getData("text");
    if (t && String(t).trim()) {
      e.preventDefault();
      setScannedText(String(t).trim());
    }
  };

  const shouldRestoreScannerFocus = () => {
    if (inputModeRef.current !== "scanner") return false;
    const t = lastPointerDownTargetRef.current;
    if (!t || typeof t.closest !== "function") return true;
    return !t.closest(
      "button, a[href], [role='button'], .MuiButtonBase-root, .MuiIconButton-root, input, textarea, select",
    );
  };

  const handleScannerBlur = () => {
    window.setTimeout(() => {
      if (!shouldRestoreScannerFocus()) return;
      focusInputNoScroll(scannerInputRef.current);
    }, 0);
  };

  return (
    <Box sx={shellSx}>
      <Box
        sx={{
          ...topBarSx,
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 1.5,
          py: 1.25,
        }}
      >
        <IconButton
          onClick={() => navigate("/ym/assemblies", { replace: false })}
          aria-label="Назад"
          sx={barIconSx}
        >
          <IconChevronLeft size={26} stroke={1.5} />
        </IconButton>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            pr: 0.5,
            minWidth: 0,
            gap: 0.35,
          }}
        >
          <Box sx={{ ...counterPillSx, minWidth: 0, maxWidth: "100%" }}>
            <Typography sx={{ ...counterPillTextSx, textAlign: "center" }}>
              {linesLoading ? "…" : `${remainingUnits} шт`}
            </Typography>
          </Box>
          {selectedCabinetId ? (
            <Box
              sx={cabinetChipSx("ym")}
              title={selectedCabinetName || `Кабинет ${selectedCabinetId}`}
            >
              <Typography noWrap sx={cabinetChipTextSx("ym")}>
                {selectedCabinetName || `Кабинет ${selectedCabinetId}`}
              </Typography>
            </Box>
          ) : null}
        </Box>
        <Tooltip
          title={
            inputMode === "camera"
              ? "Ввод со сканера Bluetooth (клавиатурный режим)"
              : "Режим камеры"
          }
        >
          <Box
            component="button"
            type="button"
            onClick={toggleInputMode}
            aria-label={
              inputMode === "camera"
                ? "Режим сканера Bluetooth"
                : "Режим камеры"
            }
            sx={{
              ...(inputMode === "camera" ? barIconSx : barIconActiveSx),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 0,
              cursor: "pointer",
            }}
          >
            {inputMode === "camera" ? (
              <IconBluetooth size={22} stroke={1.5} />
            ) : (
              <IconCamera size={22} stroke={1.5} />
            )}
          </Box>
        </Tooltip>
      </Box>

      <Box sx={{ flex: 1, px: 2, py: 2, overflow: "auto" }}>
        <Box sx={{ ...bannerSx("primary"), mb: 1.5 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={bannerTitleSx("primary")}>
              СКАНИРУЙТЕ КИЗ
            </Typography>
            <Typography sx={bannerTextSx}>
              В любом порядке — товар и заказ определятся сами. Первым
              закрывается заказ с ближайшей отгрузкой.
            </Typography>
          </Box>
        </Box>

        {inputMode === "camera" ? (
          <Box
            sx={{
              height: CAMERA_H,
              borderRadius: `${ds.radiusLg}px`,
              overflow: "hidden",
              bgcolor: camera.bg,
              border: `1px solid ${ds.border2}`,
              boxShadow: ds.shadowXs,
              position: "relative",
            }}
          >
            <video
              ref={videoRef}
              playsInline
              muted
              autoPlay
              style={{
                width: "100%",
                height: CAMERA_H,
                objectFit: "cover",
                display: "block",
              }}
            />
            {cameraError && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  px: 2,
                  bgcolor: camera.overlay,
                }}
              >
                <Typography
                  sx={{
                    color: camera.errorText,
                    textAlign: "center",
                    fontSize: "0.875rem",
                  }}
                >
                  {cameraError}
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              height: SCANNER_STRIP_H,
              minHeight: SCANNER_STRIP_H,
              maxHeight: SCANNER_STRIP_H,
              flexShrink: 0,
              boxSizing: "border-box",
              borderRadius: `${ds.radiusLg}px`,
              bgcolor: ds.surface2,
              border: `1px solid ${ds.border}`,
              px: 1,
              py: 0.5,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "stretch",
              overflow: "hidden",
            }}
          >
            <TextField
              inputRef={scannerInputRef}
              size="small"
              onKeyDown={handleScannerKeyDown}
              onPaste={handleScannerPaste}
              onBlur={handleScannerBlur}
              placeholder="Код маркировки ЧЗ…"
              fullWidth
              name="bt-scanner-capture"
              inputProps={{
                inputMode: "none",
                enterKeyHint: "done",
                autoCapitalize: "off",
                autoComplete: "off",
                autoCorrect: "off",
                spellCheck: false,
                "aria-label": "Поле ввода кода со сканера Bluetooth",
              }}
              sx={scannerFieldSx}
            />
          </Box>
        )}

        {inputMode === "camera" && decoderError && (
          <Typography
            sx={{ color: dsText.danger, fontSize: "0.8rem", mt: 1.5 }}
          >
            {decoderError}
          </Typography>
        )}

        {scanPipeline.active && (
          <Box sx={{ ...cardSx, mt: 1.5, px: 1.6, py: 1.4 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <LinearProgress
                  variant={
                    scanPipeline.indeterminate ? "indeterminate" : "determinate"
                  }
                  value={scanPipeline.indeterminate ? 0 : scanPipeline.value}
                  sx={{
                    height: 6,
                    bgcolor: ds.surface3,
                    "& .MuiLinearProgress-bar": {
                      bgcolor: scanPipeline.err
                        ? ds.danger
                        : scanPipeline.ok
                          ? ds.success
                          : ds.primary,
                    },
                  }}
                />
              </Box>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {scanPipeline.spin ? (
                  <CircularProgress size={18} sx={{ color: ds.primary }} />
                ) : scanPipeline.ok ? (
                  <IconCheck size={20} stroke={2} color={ds.success} />
                ) : scanPipeline.err ? (
                  <IconX size={20} stroke={2} color={ds.danger} />
                ) : null}
              </Box>
            </Box>
            {statusMessage ? (
              <Typography
                sx={{
                  mt: 1,
                  fontSize: "0.8125rem",
                  lineHeight: 1.4,
                  fontWeight: 600,
                  color:
                    statusTone === "error"
                      ? dsText.danger
                      : statusTone === "success"
                        ? dsText.success
                        : statusTone === "warning"
                          ? dsText.warning
                          : ds.text,
                }}
              >
                {statusMessage}
              </Typography>
            ) : null}
          </Box>
        )}

        {Object.keys(shipped).length ? (
          <>
            {/* Собранные заказы уходят из очереди (они уже не STARTED), а ярлык ещё нужен. */}
            <Typography sx={{ ...sectionLabelSx, mt: 2, mb: 0.75 }}>
              ГОТОВЫ К ОТГРУЗКЕ · {Object.keys(shipped).length}
            </Typography>
            <Box
              sx={{
                ...cardSx,
                py: 1,
                px: 1.4,
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
              }}
            >
              {Object.keys(shipped).map((id) => (
                <Box
                  key={`shipped-${id}`}
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <IconCheck
                    size={16}
                    stroke={2}
                    color={ds.success}
                    style={{ flexShrink: 0 }}
                  />
                  <Typography
                    sx={{
                      color: ds.text,
                      fontSize: "0.8125rem",
                      fontWeight: 700,
                      flex: 1,
                    }}
                  >
                    {id}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={busyOrder === id}
                    onClick={() => runForOrder(id, "label")}
                    startIcon={<IconTag size={16} stroke={1.75} />}
                    sx={{
                      borderRadius: "999px",
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    Ярлык
                  </Button>
                </Box>
              ))}
            </Box>
          </>
        ) : null}

        <Box sx={{ mt: 2 }}>
          <ManufacturerCatalogPanel
            groupByManufacturer={groupByManufacturer}
            onGroupByManufacturerChange={setGroupByManufacturer}
            catalogStack={catalogStack}
            onCatalogBreadcrumbClick={handleCatalogBreadcrumbClick}
            visibleCatalogs={visibleCatalogs}
            groups={catalogProducts}
            catalogsIndex={catalogsIndex}
            currentCatalogId={currentCatalogId}
            filteredIncomplete={filteredIncomplete}
            filteredComplete={filteredComplete}
            onEnterCatalog={handleEnterCatalog}
            linesLoading={linesLoading}
            linesError={linesError}
            renderProductCard={(product, variant) => (
              <CatalogProductCard
                key={`${product.key}-${variant}`}
                product={product}
                variant={variant}
              />
            )}
            defaultListEmpty={
              <>
                <Typography sx={{ ...sectionLabelSx, mb: 0.75 }}>
                  ЗАКАЗЫ К СБОРКЕ{orders.length ? ` · ${orders.length}` : ""}
                  {readyOrders.length ? ` · готово ${readyOrders.length}` : ""}
                </Typography>

                {linesLoading && !orders.length ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
                    <CircularProgress size={34} sx={{ color: ds.primary }} />
                  </Box>
                ) : null}
                {linesError ? (
                  <Typography sx={{ color: dsText.danger, textAlign: "center", py: 3 }}>
                    {linesError}
                  </Typography>
                ) : null}
                {!linesLoading && !linesError && orders.length === 0 ? (
                  <Typography sx={{ color: ds.text2, textAlign: "center", py: 4 }}>
                    Нет заказов к сборке
                  </Typography>
                ) : null}

                {dayGroups.map((g, index) => {
          const closed = isDayCollapsed(g.day, index);
          const dayComplete = g.done >= g.need;
          return (
            <Box key={g.day || "no-date"} sx={{ mb: 1.25 }}>
              <Box
                component="button"
                type="button"
                onClick={() =>
                  setCollapsedDays((prev) => ({ ...prev, [g.day]: !closed }))
                }
                aria-expanded={!closed}
                sx={{
                  ...cardSx,
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.6,
                  py: 1.1,
                  mb: closed ? 0 : 0.75,
                  cursor: "pointer",
                  textAlign: "left",
                  font: "inherit",
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      color: ds.text,
                      fontWeight: 700,
                      fontSize: "0.9375rem",
                    }}
                  >
                    {shipmentDayLabel(g.day)}
                  </Typography>
                  <Typography sx={{ ...mutedTextSx, mt: 0.25 }}>
                    Заказов {g.orders.length}
                    {g.need ? ` · КИЗ ${g.done} из ${g.need}` : " · без КИЗ"}
                  </Typography>
                </Box>
                {g.need && dayComplete ? (
                  <Chip
                    label="Готово"
                    size="small"
                    sx={softChipSx("success")}
                  />
                ) : null}
                <IconChevronDown
                  size={20}
                  stroke={1.75}
                  color={ds.text2}
                  style={{
                    flexShrink: 0,
                    transition: "transform 0.2s",
                    transform: closed ? "rotate(0deg)" : "rotate(180deg)",
                  }}
                />
              </Box>
              <Collapse in={!closed} unmountOnExit>
                {g.orders.map((o) => {
                  const key = String(o.order_id);
                  const complete = o.done >= o.need;
                  // Раскладку можно отправить, когда есть все обязательные КИЗ; необязательные — по желанию.
                  const canApply = o.requiredLeft <= 0;
                  const busy = busyOrder === key;
                  const highlighted = lastOrder === key;
                  const check = checks[key];
                  const isShipped = Boolean(shipped[key]);
                  return (
                    <Box
                      key={key}
                      sx={{
                        ...(complete
                          ? cardDoneSx
                          : highlighted
                            ? cardActiveSx
                            : cardSx),
                        mb: 1,
                        py: 1.1,
                        px: 1.4,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          gap: 0.75,
                        }}
                      >
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            sx={{
                              color: ds.text,
                              fontSize: "0.8125rem",
                              fontWeight: 700,
                              lineHeight: 1.3,
                            }}
                          >
                            {o.order_id}
                          </Typography>
                          <Typography sx={{ ...mutedTextSx, mt: 0.25 }}>
                            {[orderedLabel(o.ordered_at), shipmentLabel(o.shipment_date)]
                              .filter(Boolean)
                              .join(" · ")}
                            {o.products.length > 1
                              ? ` · товаров ${o.products.length}`
                              : ""}
                          </Typography>
                        </Box>
                        <Chip
                          label={
                            isShipped
                              ? "Готов к отгрузке"
                              : o.need === 0
                                ? "Без КИЗ"
                                : complete
                                  ? "Готово"
                                  : `${o.done} из ${o.need}`
                          }
                          size="small"
                          sx={softChipSx(
                            complete || isShipped ? "success" : "primary",
                          )}
                        />
                      </Box>

                      <Box
                        sx={{
                          mt: 0.75,
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.35,
                        }}
                      >
                        {o.products.map((pr) => (
                          <Box
                            key={`${key}-${pr.item_id}`}
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 0.75,
                              minWidth: 0,
                            }}
                          >
                            <IconPackages
                              size={14}
                              stroke={1.75}
                              color={ds.text3}
                              aria-hidden
                              style={{ flexShrink: 0, marginTop: 2 }}
                            />
                            {/* Название показываем целиком: кладовщику надо отличать вкусы
                        и граммовки, а они как раз в хвосте строки. */}
                            <Typography
                              sx={{
                                ...mutedTextSx,
                                flex: 1,
                                minWidth: 0,
                                whiteSpace: "normal",
                                overflowWrap: "anywhere",
                              }}
                            >
                              {pr.title || pr.offer_id}
                              {pr.marking && !pr.expected_gtin
                                ? " · нет GTIN на складе"
                                : ""}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.72rem",
                                fontWeight: 700,
                                fontFeatureSettings: '"tnum"',
                                flexShrink: 0,
                                color:
                                  !pr.marking || pr.done >= pr.need
                                    ? dsText.success
                                    : ds.text2,
                              }}
                            >
                              {pr.marking
                                ? `${pr.done}/${pr.need}`
                                : `${pr.qty} шт`}
                            </Typography>
                          </Box>
                        ))}
                      </Box>

                      {o.otherIdentifiers.length ? (
                        <Typography
                          sx={{
                            ...mutedTextSx,
                            mt: 0.5,
                            color: dsText.warning,
                          }}
                        >
                          Маркет ждёт ещё {o.otherIdentifiers.join(", ")} — с
                          этого экрана они не передаются.
                        </Typography>
                      ) : null}

                      {check?.invalid?.length ? (
                        <Box
                          sx={{
                            mt: 0.75,
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.35,
                          }}
                        >
                          {check.invalid.map((c, i) => (
                            <Box
                              key={`${key}-bad-${c.scan_id ?? i}`}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                              }}
                            >
                              <IconX
                                size={14}
                                stroke={2}
                                color={ds.danger}
                                style={{ flexShrink: 0 }}
                              />
                              <Typography
                                sx={{
                                  ...mutedTextSx,
                                  flex: 1,
                                  color: dsText.danger,
                                }}
                              >
                                {c.title}: {c.reason || c.status}
                              </Typography>
                              {c.scan_id ? (
                                <Button
                                  size="small"
                                  variant="text"
                                  disabled={busy}
                                  onClick={() =>
                                    runForOrder(o.order_id, "unscan", {
                                      scanId: c.scan_id,
                                    })
                                  }
                                  sx={{
                                    textTransform: "none",
                                    fontWeight: 600,
                                    fontSize: "0.72rem",
                                  }}
                                >
                                  Снять
                                </Button>
                              ) : null}
                            </Box>
                          ))}
                        </Box>
                      ) : null}

                      {canApply && !isShipped ? (
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.75,
                            mt: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          <Button
                            size="small"
                            variant="contained"
                            disabled={busy}
                            onClick={() => runForOrder(o.order_id, "apply")}
                            startIcon={
                              busy ? (
                                <CircularProgress
                                  size={14}
                                  sx={{ color: "inherit" }}
                                />
                              ) : (
                                <IconScan size={16} stroke={1.75} />
                              )
                            }
                            sx={{
                              borderRadius: "999px",
                              textTransform: "none",
                              fontWeight: 600,
                              bgcolor: ds.primary,
                              "&:hover": { bgcolor: ds.primaryHover },
                            }}
                          >
                            {complete ? "В Маркет" : "В Маркет без части КИЗ"}
                          </Button>
                          {check?.pending ? (
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={busy}
                              onClick={() => runForOrder(o.order_id, "check")}
                              sx={{
                                borderRadius: "999px",
                                textTransform: "none",
                                fontWeight: 600,
                              }}
                            >
                              Проверить
                            </Button>
                          ) : null}
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={busy || !check?.ready_to_ship}
                            onClick={() => runForOrder(o.order_id, "ship")}
                            startIcon={
                              <IconTruckDelivery size={16} stroke={1.75} />
                            }
                            sx={{
                              borderRadius: "999px",
                              textTransform: "none",
                              fontWeight: 600,
                            }}
                          >
                            Готов к отгрузке
                          </Button>
                        </Box>
                      ) : null}

                      {o.done > 0 && !isShipped ? (
                        <Button
                          size="small"
                          variant="text"
                          disabled={busy}
                          onClick={() => runForOrder(o.order_id, "unscan")}
                          sx={{
                            mt: 0.5,
                            textTransform: "none",
                            fontWeight: 600,
                            color: ds.text3,
                            fontSize: "0.72rem",
                          }}
                        >
                          Снять коды
                        </Button>
                      ) : null}
                    </Box>
                  );
                })}
              </Collapse>
            </Box>
          );
        })}
              </>
            }
          />
        </Box>
      </Box>
    </Box>
  );
}
